/**
 * 학생 학습 데이터 요약 컴포넌트
 * Student Study Summary Component
 *
 * 상담 작성 시 학생의 최근 학습 데이터를 표시
 */

import React, { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  AcademicCapIcon,
  TrophyIcon,
  ClockIcon,
  UserCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../config/api';

// ==========================================
// Types
// ==========================================

interface StudentStudySummary {
  student_id: string;
  student_name: string;
  summary_period_days: number;

  // 학습 시간
  avg_study_hours: number;
  total_study_hours: number;

  // 목표 달성
  goal_achievement_rate?: number;
  daily_goal_hours?: number;

  // 출석
  attendance_rate?: number;
  total_attendance_days?: number;

  // 집중도
  focus_score?: number;
  avg_focus_rate?: number;

  // 성적 추이
  grade_trend?: 'improving' | 'stable' | 'declining';
  latest_avg_score?: number;

  // 낮은 지표
  low_metrics: string[];
}

interface StudentStudySummaryProps {
  studentId: string;
  studentName?: string;
  days?: number;  // 요약 기간 (기본 14일)
  collapsed?: boolean;  // 초기 접힘 상태
}

// ==========================================
// Component
// ==========================================

export const StudentStudySummary: React.FC<StudentStudySummaryProps> = ({
  studentId,
  studentName,
  days = 14,
  collapsed = false,
}) => {
  const [summary, setSummary] = useState<StudentStudySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(collapsed);

  // ==========================================
  // 데이터 로드
  // ==========================================

  useEffect(() => {
    loadSummary();
  }, [studentId, days]);

  const loadSummary = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_BASE_URL}/v1/owner/students/${studentId}/study-summary?days=${days}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!response.ok) {
        throw new Error('학습 데이터를 불러오지 못했습니다');
      }

      const data = await response.json();
      setSummary(data);
    } catch (err) {
      console.error('학습 요약 로드 실패:', err);
      setError(err instanceof Error ? err.message : '알 수 없는 오류');
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // Render Helpers
  // ==========================================

  const getStatusColor = (value: number, threshold: number, reverse = false): string => {
    if (reverse) {
      return value >= threshold ? 'text-green-600' : 'text-amber-600';
    }
    return value >= threshold ? 'text-green-600' : 'text-red-600';
  };

  const getGradeTrendIcon = (trend?: string) => {
    if (trend === 'improving') return '📈';
    if (trend === 'declining') return '📉';
    return '➡️';
  };

  const getFocusStars = (score?: number): string => {
    if (!score) return '☆☆☆☆☆';
    const filled = Math.round(score);
    return '⭐'.repeat(filled) + '☆'.repeat(5 - filled);
  };

  // ==========================================
  // Render
  // ==========================================

  if (loading) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
          <span className="text-sm text-blue-600">학습 데이터 불러오는 중...</span>
        </div>
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 mt-0.5" />
          <div>
            <p className="text-sm text-amber-800 font-medium">학습 데이터를 불러올 수 없습니다</p>
            <p className="text-xs text-amber-600 mt-1">{error || '데이터가 없습니다'}</p>
            <button
              onClick={loadSummary}
              className="text-xs text-amber-700 underline mt-2 hover:text-amber-900"
            >
              다시 시도
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg">
      {/* 헤더 (접기/펼치기) */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-blue-100 transition-colors"
      >
        <div className="flex items-center space-x-2">
          <ChartBarIcon className="h-5 w-5 text-blue-600" />
          <span className="font-medium text-blue-900">
            📊 최근 {summary.summary_period_days}일 학습 데이터
          </span>
        </div>
        {isCollapsed ? (
          <ChevronDownIcon className="h-5 w-5 text-blue-600" />
        ) : (
          <ChevronUpIcon className="h-5 w-5 text-blue-600" />
        )}
      </button>

      {/* 내용 */}
      {!isCollapsed && (
        <div className="px-4 pb-4 space-y-3">
          {/* 핵심 지표 그리드 */}
          <div className="grid grid-cols-2 gap-3">
            {/* 평균 공부 시간 */}
            <div className="bg-white rounded-lg p-3 border border-blue-100">
              <div className="flex items-center space-x-2 mb-1">
                <ClockIcon className="h-4 w-4 text-blue-600" />
                <span className="text-xs text-gray-600">평균 공부시간</span>
              </div>
              <div className="flex items-baseline space-x-1">
                <span
                  className={`text-2xl font-bold ${getStatusColor(
                    summary.avg_study_hours,
                    summary.daily_goal_hours || 8
                  )}`}
                >
                  {summary.avg_study_hours}h
                </span>
                <span className="text-xs text-gray-500">/일</span>
              </div>
              {summary.daily_goal_hours && (
                <div className="text-xs text-gray-500 mt-1">
                  목표: {summary.daily_goal_hours}h
                </div>
              )}
            </div>

            {/* 목표 달성률 */}
            {summary.goal_achievement_rate !== undefined && (
              <div className="bg-white rounded-lg p-3 border border-blue-100">
                <div className="flex items-center space-x-2 mb-1">
                  <TrophyIcon className="h-4 w-4 text-blue-600" />
                  <span className="text-xs text-gray-600">목표 달성률</span>
                </div>
                <div className="flex items-baseline space-x-1">
                  <span
                    className={`text-2xl font-bold ${getStatusColor(
                      summary.goal_achievement_rate,
                      80
                    )}`}
                  >
                    {summary.goal_achievement_rate}%
                  </span>
                </div>
              </div>
            )}

            {/* 출석률 */}
            {summary.attendance_rate !== undefined && (
              <div className="bg-white rounded-lg p-3 border border-blue-100">
                <div className="flex items-center space-x-2 mb-1">
                  <UserCircleIcon className="h-4 w-4 text-blue-600" />
                  <span className="text-xs text-gray-600">출석률</span>
                </div>
                <div className="flex items-baseline space-x-1">
                  <span
                    className={`text-2xl font-bold ${getStatusColor(
                      summary.attendance_rate,
                      80
                    )}`}
                  >
                    {summary.attendance_rate}%
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {summary.total_attendance_days}일 출석
                </div>
              </div>
            )}

            {/* 집중도 */}
            {summary.focus_score !== undefined && (
              <div className="bg-white rounded-lg p-3 border border-blue-100">
                <div className="flex items-center space-x-2 mb-1">
                  <AcademicCapIcon className="h-4 w-4 text-blue-600" />
                  <span className="text-xs text-gray-600">집중도</span>
                </div>
                <div className="text-lg">
                  {getFocusStars(summary.focus_score)}
                </div>
                {summary.avg_focus_rate && (
                  <div className="text-xs text-gray-500 mt-1">
                    집중률: {summary.avg_focus_rate}%
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 성적 추이 (있는 경우) */}
          {summary.grade_trend && summary.latest_avg_score && (
            <div className="bg-white rounded-lg p-3 border border-blue-100">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs text-gray-600">최근 성적 추이</span>
                  <div className="text-sm font-medium mt-1">
                    평균 {summary.latest_avg_score}점 {getGradeTrendIcon(summary.grade_trend)}
                    <span className="ml-2 text-xs text-gray-500">
                      {summary.grade_trend === 'improving' && '향상중'}
                      {summary.grade_trend === 'stable' && '유지중'}
                      {summary.grade_trend === 'declining' && '하락중'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 낮은 지표 경고 */}
          {summary.low_metrics && summary.low_metrics.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <ExclamationTriangleIcon className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-amber-800 mb-1">
                    💡 주의가 필요한 영역
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {summary.low_metrics.map((metric, index) => (
                      <span
                        key={index}
                        className="inline-block px-2 py-0.5 bg-amber-100 text-amber-800 text-xs rounded"
                      >
                        {metric}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-amber-700 mt-2">
                    → 상담 주제로 활용하세요
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* 전체 데이터 보기 버튼 */}
          <button
            onClick={() => {
              // TODO: 학습 분석 페이지로 이동
              window.open(`/owner/analytics?student_id=${studentId}`, '_blank');
            }}
            className="w-full text-center text-xs text-blue-600 hover:text-blue-800 underline py-2"
          >
            전체 학습 데이터 보기 →
          </button>
        </div>
      )}
    </div>
  );
};
